import { Component, forwardRef, Injector, input, OnChanges, OnInit, signal, SimpleChanges } from '@angular/core';
import { ListConstants } from '../../core/constants/lists.constants';
import { StateProvinceModel } from '../../core/models/data/state-province.model';
import { PaginatedListModel } from '../../core/models/service/paginated-list.model';
import { LocationService } from '../../core/services/location.service';
import { CountryModel } from '../../core/models/data/country.model';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, NgControl, FormsModule, ReactiveFormsModule, Validators, NG_VALIDATORS, Validator, AbstractControl, ValidationErrors } from '@angular/forms';
import { FormValidatorMessages } from '../../core/enums/forms.enums';
import { CommonChecks } from '../../core/utilities/common-checks.utilities';
import { ServiceResultModel } from '../../core/models/service/service-result.model';

@Component({
  selector: 'ms-state-province-dropdown',  
  imports: [FormsModule, ReactiveFormsModule],
  templateUrl: './state-province-dropdown.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StateProvinceDropdown),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => StateProvinceDropdown),
      multi: true,
    },
  ]
})
export class StateProvinceDropdown implements OnInit, OnChanges, ControlValueAccessor, Validator {
  public selectedStateProvince: StateProvinceModel | undefined = undefined;
  public selectedStateProvinceId = signal<string | undefined>("");
  public stateProvincesList = signal<StateProvinceModel[] | undefined>(undefined);
  public selectedCountry = input<CountryModel | undefined>(undefined);

  public isLoading: boolean = false;

  public stateProvince = new FormControl('');
  public parentControl: AbstractControl | null = null;

  private ngControl: NgControl | null = null;
  private onChange = (_value: any) => { };
  private onTouchedCallback = () => { };
  private pendingStateProvince: StateProvinceModel | undefined = undefined;

  constructor(private injector: Injector, private locationsService: LocationService) { }

  public ngOnInit(): void {
    this.ngControl = this.injector.get(NgControl, null);

    this.stateProvince.valueChanges.subscribe(value => {      
      this.onChange(value);
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedCountry']) {
      this.reset();
      this.loadStateProvinces();
    }
  }

  public async loadStateProvinces(): Promise<void> {
    this.isLoading = true;

    if (!this.selectedCountry()) {
      this.isLoading = false;
      return;
    }

    let response: ServiceResultModel<PaginatedListModel<StateProvinceModel>> = await this.locationsService.searchStateProvinces({ countryIds: [this.selectedCountry()!.countryId] }, ListConstants.STATE_PROVINCES_ORDERBY_DEFAULT(), 0, 150);

    if (response?.data?.data) {
      this.stateProvincesList.set(response.data?.data);
    }

    if (this.pendingStateProvince) {
      const pending = this.pendingStateProvince;
      this.pendingStateProvince = undefined;
      this.selectedStateProvince = pending;
      const idString = pending.stateProvinceId?.toString() || "";
      this.selectedStateProvinceId.set(idString);
      this.stateProvince.setValue(idString, { emitEvent: false });
    }

    this.isLoading = false;
  }

  public onSelectionChange(event: Event): void {
    const target = event.target as HTMLSelectElement;

    const stateProvinceId = target.value;
    const stateProvince = this.stateProvincesList()?.find(sp => sp.stateProvinceId === +stateProvinceId)    
    
    this.selectedStateProvince = stateProvince;

    if(!stateProvince) {
      this.selectedStateProvinceId.set("");
    } else {
      this.selectedStateProvinceId.set(stateProvince?.stateProvinceId.toString());
    }

    this.onChange(stateProvince);
  }

  public reset(): void {
    this.selectedStateProvince = undefined;
    this.stateProvincesList.set([]);
    this.selectedStateProvinceId.set("");
    this.stateProvince.setValue("", { emitEvent: false });
  }

  public get touched(): boolean {
    return this.ngControl?.control?.touched ?? false;
  }

  public writeValue(value: any): void {
    if (value === "" || value === null || value === undefined) {
      this.selectedStateProvince = undefined;
      this.selectedStateProvinceId.set("");
      this.pendingStateProvince = undefined;
      this.stateProvince.setValue("", { emitEvent: false });
    } else {
      this.selectedStateProvince = value;
      const idString = value?.stateProvinceId?.toString() || "";
      this.selectedStateProvinceId.set(idString);
      this.stateProvince.setValue(idString, { emitEvent: false });
      this.pendingStateProvince = value;
    }
  }

  public registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  public get hasErrors(): boolean {
    let errorCount = 0; 

    if(this.ngControl?.control?.errors) {
      errorCount = Object.keys(this.ngControl?.control?.errors).length;
    }

    return this.touched && errorCount > 0;
  }

  public validate(control: AbstractControl): ValidationErrors | null {
    this.parentControl = control;
    return null;
  }

  public get required(): boolean {
    return this.ngControl?.control?.hasValidator(Validators.required) || false;
  }

  public get isValid(): boolean {
    return this.touched && !CommonChecks.isNullOrUndefined(this.selectedStateProvince) && this.ngControl?.control?.errors == null;
  }

  public onBlur(): void {
    this.onTouchedCallback();
  }

  public getErrors(): string[] {
    const errors: string[] = [];
    const controlErrors = this.ngControl?.control?.errors;

    if (controlErrors) {
      if (controlErrors['required']) {
        errors.push(controlErrors['required'].message || FormValidatorMessages.StateProvinceRequired);
      }
    }

    return errors;
  }
}
